/**
 * Life Storybook — the diary theme's rendering of the Life Map.
 *
 * The graph view answers "how does everything connect"; this view answers
 * "what is the story so far". Each dream is a chapter; its goals, habits,
 * tasks and skills are entries written on the chapter's page in the
 * handwritten voice. Items that belong to no dream gather in two closing
 * chapters ("Loose pages", "Day to day").
 *
 * Deliberate scope: dreams, goals, tasks, habits, skills — the storybook
 * is a reading view, not a census. Aspirations (legacy, folded into
 * goals), roadblocks and action items stay on the graph view.
 *
 * Data comes from the per-type saas endpoints (not the graph payload,
 * which carries no dates/descriptions), joined client-side by the
 * parent_*_id fields.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, RefreshCw } from 'lucide-react'
import { DreamService, type Dream } from '@/services/dreamService'
import { AspirationService, type Aspiration } from '@/services/aspirationService'
import { GoalService, type Goal } from '@/services/goalService'
import { TaskService, type Task } from '@/services/taskService'
import { HabitService, type Habit } from '@/services/habitService'
import { SkillService, type Skill } from '@/services/skillService'

type EntryKind = 'goal' | 'task' | 'habit' | 'skill'

interface StoryEntry {
  kind: EntryKind
  id: number | string
  title: string
  status: string
  meta?: string
  marginNote?: string
  route: string
  /** Entries indented under a goal on the page. */
  children?: StoryEntry[]
}

interface StoryChapter {
  key: string
  kicker: string
  title: string
  emoji: string
  route?: string
  status?: string
  progressLine?: string
  entries: StoryEntry[]
}

const DOODLES: Record<EntryKind, string> = {
  goal: '🎯',
  task: '📝',
  habit: '🔁',
  skill: '🧠',
}

const DONE_STATUSES = new Set(['completed', 'internalized', 'sufficient_for_goal'])
// 'paused_by_user' is the canonical spelling of the attention pause since the
// scheduling constitution freed the word HOLD for the calendar dimension (§2);
// 'hold' / 'on_hold' are the legacy spellings still present on stored rows, so
// all three must stamp "resting". Authority for the status vocabulary is
// src/domains/shared_utilities/services/active_status.py — the frontend has no
// shared constant for it, so this local set has to be kept in step by hand.
const RESTING_STATUSES = new Set([
  'paused', 'paused_by_user', 'parked', 'hold', 'on_hold', 'archived', 'cancelled', 'resting',
])

function isDone(status: string): boolean {
  return DONE_STATUSES.has(status)
}

function stampFor(status: string): { text: string; className: string } | null {
  if (isDone(status)) return { text: 'done', className: 'yn-stamp-done' }
  if (RESTING_STATUSES.has(status)) return { text: 'resting', className: 'yn-stamp-resting' }
  if (status === 'in_progress' || status === 'forming') {
    return { text: 'underway', className: 'yn-stamp-doing' }
  }
  if (status === 'chat_suggested') return { text: 'an idea', className: 'yn-stamp-new' }
  return null
}

function prettyDate(iso?: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function taskEntry(t: Task): StoryEntry {
  const due = prettyDate(t.due_date)
  return {
    kind: 'task',
    id: t.id,
    title: t.title,
    status: t.status,
    meta: due ? `due ${due}` : undefined,
    route: `/tasks/${t.id}`,
  }
}

const CADENCE_WORDS: Record<string, string> = {
  daily: 'every day',
  weekly: 'every week',
  biweekly: 'every other week',
  monthly: 'every month',
}

function habitEntry(h: Habit): StoryEntry {
  const cadence = h.target_cadence
    ? CADENCE_WORDS[h.target_cadence] ?? h.target_cadence
    : null
  const window = h.preferred_time_window ? `in the ${h.preferred_time_window}` : null
  return {
    kind: 'habit',
    id: h.id,
    title: h.title,
    status: h.status,
    marginNote: [cadence, window].filter(Boolean).join(', ') || undefined,
    route: `/habits/${h.id}`,
  }
}

function skillEntry(s: Skill): StoryEntry {
  return {
    kind: 'skill',
    id: s.id,
    title: s.title,
    status: s.status,
    meta: s.current_level && s.target_level ? `${s.current_level} → ${s.target_level}` : undefined,
    route: `/skills/${s.id}`,
  }
}

export function LifeStorybook() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dreams, setDreams] = useState<Dream[]>([])
  const [aspirations, setAspirations] = useState<Aspiration[]>([])
  const [goals, setGoals] = useState<Goal[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [habits, setHabits] = useState<Habit[]>([])
  const [skills, setSkills] = useState<Skill[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const dreamService = new DreamService()
      const aspirationService = new AspirationService()
      const goalService = new GoalService()
      const taskService = new TaskService()
      const habitService = new HabitService()
      const skillService = new SkillService()
      // Each list degrades to empty on its own failure so one broken
      // endpoint doesn't blank the whole book — but if EVERY fetch fails
      // that's an outage, not a fresh account, and it must read as an
      // error rather than "your story hasn't started yet".
      const settled = await Promise.allSettled([
        dreamService.getDreams({ include: 'all' }),
        aspirationService.getAspirations(),
        goalService.getGoals(),
        taskService.getTasks(),
        habitService.getHabits(),
        skillService.getSkills(),
      ])
      if (settled.every((r) => r.status === 'rejected')) {
        throw new Error('the server could not be reached')
      }
      const pick = <T,>(r: PromiseSettledResult<T[]>): T[] =>
        r.status === 'fulfilled' ? r.value : []
      setDreams(pick(settled[0] as PromiseSettledResult<Dream[]>))
      setAspirations(pick(settled[1] as PromiseSettledResult<Aspiration[]>))
      setGoals(pick(settled[2] as PromiseSettledResult<Goal[]>))
      setTasks(pick(settled[3] as PromiseSettledResult<Task[]>))
      setHabits(pick(settled[4] as PromiseSettledResult<Habit[]>))
      setSkills(pick(settled[5] as PromiseSettledResult<Skill[]>))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open your book')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const chapters = useMemo<StoryChapter[]>(() => {
    const goalIdsPlaced = new Set<number>()
    const taskIdsPlaced = new Set<number>()
    const habitIdsPlaced = new Set<number | string>()
    const skillIdsPlaced = new Set<number | string>()

    // Guards on every push: an item can carry both a dream and a goal
    // parent (independent optional fields), and without the placed-set
    // check it would be written into the book twice — with a duplicate
    // React key to match.
    const entriesForGoal = (goal: Goal): StoryEntry[] => {
      const kids: StoryEntry[] = []
      for (const t of tasks) {
        if (!taskIdsPlaced.has(t.id) && t.parent_goal_id === goal.id) {
          kids.push(taskEntry(t)); taskIdsPlaced.add(t.id)
        }
      }
      for (const h of habits) {
        if (!habitIdsPlaced.has(h.id) && h.parent_type === 'goal' && Number(h.parent_id) === goal.id) {
          kids.push(habitEntry(h)); habitIdsPlaced.add(h.id)
        }
      }
      for (const s of skills) {
        if (!skillIdsPlaced.has(s.id) && ((s.parent?.type === 'goal' && Number(s.parent.id) === goal.id) || Number(s.parent_goal_id) === goal.id)) {
          kids.push(skillEntry(s)); skillIdsPlaced.add(s.id)
        }
      }
      return kids
    }

    const goalEntry = (g: Goal): StoryEntry => {
      goalIdsPlaced.add(g.id)
      const target = prettyDate(g.target_date)
      return {
        kind: 'goal',
        id: g.id,
        title: g.title,
        status: g.status,
        meta: target ? `aiming for ${target}` : undefined,
        route: `/goals/${g.id}`,
        children: entriesForGoal(g),
      }
    }

    // Dreams arrive with include:'all' (every lifecycle state) so completed
    // and still-just-an-idea dreams keep their chapters — but they must
    // read that way: living chapters first, then ideas, then the resting
    // and finished ones, each stamped.
    const chapterRank = (status: string): number => {
      if (isDone(status)) return 3
      if (RESTING_STATUSES.has(status)) return 2
      if (status === 'chat_suggested') return 1
      return 0
    }
    const orderedDreams = [...dreams].sort(
      (a, b) => chapterRank(a.status) - chapterRank(b.status),
    )

    // Aspirations are legacy (their pages redirect to /goals) but old data
    // still parents goals through them — follow aspiration.parent_dream_id
    // so those goals land in their dream's chapter, not "Loose pages".
    const aspToDream = new Map<number, number | undefined>(
      aspirations.map((a) => [a.id, a.parent_dream_id]),
    )
    const goalBelongsToDream = (g: Goal, dreamId: number): boolean =>
      g.parent_dream_id === dreamId ||
      (g.parent_aspiration_id != null && aspToDream.get(g.parent_aspiration_id) === dreamId)

    const result: StoryChapter[] = orderedDreams.map((dream, i) => {
      const entries: StoryEntry[] = []
      for (const g of goals) {
        if (!goalIdsPlaced.has(g.id) && goalBelongsToDream(g, dream.id)) {
          entries.push(goalEntry(g))
        }
      }
      for (const t of tasks) {
        if (!taskIdsPlaced.has(t.id) && t.parent_dream_id === dream.id) {
          entries.push(taskEntry(t)); taskIdsPlaced.add(t.id)
        }
      }
      for (const h of habits) {
        if (!habitIdsPlaced.has(h.id) && h.parent_type === 'dream' && Number(h.parent_id) === dream.id) {
          entries.push(habitEntry(h)); habitIdsPlaced.add(h.id)
        }
      }
      for (const s of skills) {
        if (!skillIdsPlaced.has(s.id) && !s.parent_goal_id && ((s.parent?.type === 'dream' && Number(s.parent.id) === dream.id) || Number(s.parent_dream_id) === dream.id)) {
          entries.push(skillEntry(s)); skillIdsPlaced.add(s.id)
        }
      }
      const flat = entries.flatMap((e) => [e, ...(e.children ?? [])])
      const done = flat.filter((e) => isDone(e.status)).length
      return {
        key: `dream-${dream.id}`,
        kicker: `Chapter ${i + 1}`,
        title: dream.title,
        emoji: '✨',
        route: `/dreams/${dream.id}`,
        status: dream.status,
        progressLine: flat.length > 0
          ? `${done} of ${flat.length} pages written`
          : 'a blank page, waiting',
        entries,
      }
    })

    const looseGoals = goals.filter((g) => !goalIdsPlaced.has(g.id))
    if (looseGoals.length > 0) {
      result.push({
        key: 'loose-pages',
        kicker: 'Between chapters',
        title: 'Loose pages',
        emoji: '📎',
        entries: looseGoals.map(goalEntry),
      })
    }

    const daily: StoryEntry[] = [
      ...habits.filter((h) => !habitIdsPlaced.has(h.id)).map(habitEntry),
      ...skills.filter((s) => !skillIdsPlaced.has(s.id)).map(skillEntry),
      ...tasks.filter((t) => !taskIdsPlaced.has(t.id)).map(taskEntry),
    ]
    if (daily.length > 0) {
      result.push({
        key: 'day-to-day',
        kicker: 'Every day',
        title: 'Day to day',
        emoji: '🌤️',
        entries: daily,
      })
    }

    return result
  }, [dreams, aspirations, goals, tasks, habits, skills])

  const renderEntry = (entry: StoryEntry, depth = 0) => {
    const stamp = stampFor(entry.status)
    const done = isDone(entry.status)
    return (
      <div key={`${entry.kind}-${entry.id}`}>
        <button
          type="button"
          onClick={() => navigate(entry.route)}
          className="yn-entry w-full text-left block"
          style={depth > 0 ? { paddingLeft: 34 + depth * 22 } : undefined}
        >
          <span className="yn-entry-doodle" style={depth > 0 ? { left: 6 + depth * 22 } : undefined} aria-hidden>
            {done && entry.kind === 'task' ? '✅' : DOODLES[entry.kind]}
          </span>
          <span className="flex items-baseline gap-2 flex-wrap">
            <span className={`yn-entry-title ${done ? 'yn-entry-title-done' : ''}`}>
              {entry.title}
            </span>
            {stamp && <span className={`yn-entry-stamp ${stamp.className}`}>{stamp.text}</span>}
            {entry.meta && <span className="yn-entry-meta">{entry.meta}</span>}
          </span>
          {entry.marginNote && (
            <span className="yn-entry-margin-note block mt-0.5">↳ {entry.marginNote}</span>
          )}
        </button>
        {(entry.children ?? []).map((c) => renderEntry(c, depth + 1))}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-16 justify-center yn-entry-meta text-base">
        <Loader2 className="w-5 h-5 animate-spin" /> Opening your book…
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-12 text-center space-y-3">
        <p className="yn-entry-meta text-base">The book wouldn’t open: {error}</p>
        <button type="button" onClick={() => void load()} className="yn-entry-stamp yn-stamp-doing">
          try again
        </button>
      </div>
    )
  }

  if (chapters.length === 0) {
    return (
      <div className="py-16 text-center">
        <div className="text-4xl mb-3" aria-hidden>📖</div>
        <p className="yn-chapter-title text-2xl">Your story hasn’t started yet</p>
        <p className="yn-entry-meta text-sm mt-2">
          The first dream you write down becomes Chapter 1.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-5 max-w-3xl mx-auto w-full">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void load()}
          className="p-1.5 rounded hover:bg-black/5"
          style={{ color: 'var(--ink-faded)' }}
          aria-label="Re-read the book"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {chapters.map((ch) => {
        const chapterStamp = ch.status ? stampFor(ch.status) : null
        return (
        <section key={ch.key} className="yn-chapter p-4 sm:p-5">
          <header className="text-center mb-3">
            <div className="yn-chapter-kicker">{ch.kicker}</div>
            {ch.route ? (
              <button
                type="button"
                onClick={() => navigate(ch.route!)}
                className="yn-chapter-title text-2xl sm:text-3xl hover:underline decoration-dotted"
              >
                <span aria-hidden>{ch.emoji} </span>{ch.title}
              </button>
            ) : (
              <div className="yn-chapter-title text-2xl sm:text-3xl">
                <span aria-hidden>{ch.emoji} </span>{ch.title}
              </div>
            )}
            {chapterStamp && (
              <div className="mt-1">
                <span className={`yn-entry-stamp ${chapterStamp.className}`}>{chapterStamp.text}</span>
              </div>
            )}
            {ch.progressLine && <div className="yn-entry-meta mt-1">{ch.progressLine}</div>}
            <div className="yn-chapter-flourish mt-1" aria-hidden>─── ✦ ───</div>
          </header>
          {ch.entries.length > 0 ? (
            <div>{ch.entries.map((e) => renderEntry(e))}</div>
          ) : (
            <p className="yn-entry-meta text-center pb-2">Nothing written here yet.</p>
          )}
        </section>
        )
      })}
    </div>
  )
}

export default LifeStorybook
